/**
 * @module estrutura/label/estruturalabelediting
 */

import { Plugin } from 'ckeditor5/src/core';
import { DowncastWriter, Element } from 'ckeditor5/src/engine';
import { toWidget, viewToModelPositionOutsideModelElement, Widget } from 'ckeditor5/src/widget';
import EstruturaLabelCommand from '../commands/estruturalabelcommand';
import EstruturaEditing from '../estruturaediting';
import { Dispositivo, Estrutura } from '../models';
import { getEstruturaLabelStr } from './utils';


const ESTRUTURA = 'estrutura';
const ESTRUTURA_LABEL = 'estruturaLabel';

/**
 * The estruturaLabel editing.
 *
 * Introduces the `'estruturaLabel'` model element.
 *
 * @extends {Plugin}
 */
export default class EstruturaLabelEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'EstruturaLabelEditing';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ EstruturaEditing, Widget ];
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );
		/**
		 * @type {WeakMap<Estrutura, Array<Element>>}
		 */
		this._estruturaLabels = new WeakMap();
		this._config = editor.config.get( ESTRUTURA ) || {};
		/**
		 * @type {Map<number, Dispositivo>}
		 */
		this._dispositivos = new Map();
		for ( const disp of this._config.dispositivos ) {
			this._dispositivos.set( disp.codigo, disp );
		}
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		this._defineSchema();
		this._defineConverters();

		editor.commands.add( ESTRUTURA_LABEL, new EstruturaLabelCommand( editor ) );
		editor.editing.mapper.on(
			'viewToModelPosition',
			viewToModelPositionOutsideModelElement( editor.model, viewElement => viewElement.hasClass( ESTRUTURA_LABEL ) )
		);
	}

	/**
	 * @private
	 */
	_defineSchema() {
		const schema = this.editor.model.schema;
		schema.register( ESTRUTURA_LABEL, {
			allowWhere: '$text',
			allowAttributesOf: '$text',
			isInline: true,
			isObject: true
		} );
	}

	/**
	 * @private
	 */
	_defineConverters() {
		const conversion = this.editor.conversion;
		conversion.for( 'upcast' ).elementToElement( {
			view: {
				name: 'span',
				classes: [ ESTRUTURA_LABEL ]
			},
			model: ESTRUTURA_LABEL
		} );

		conversion.for( 'dataDowncast' )
			.elementToElement( {
				model: ESTRUTURA_LABEL,
				view: ( modelItem, conversionApi ) => this._createEstruturaLabelViewElement( modelItem, conversionApi.writer, false )
			} );

		conversion.for( 'editingDowncast' )
			.elementToElement( {
				model: ESTRUTURA_LABEL,
				view: ( modelItem, conversionApi ) => toWidget( this._createEstruturaLabelViewElement( modelItem, conversionApi.writer, true ), conversionApi.writer )
			} )
			.add( dispatcher => {
				dispatcher.on( `attribute:${ ESTRUTURA }:${ ESTRUTURA }`, ( evt, data, conversionApi ) => {
					/**
					 * @type {Estrutura}
					 */
					const est = data.attributeNewValue;
					const labels = this._estruturaLabels.get( data.attributeOldValue ) || [];
					this._estruturaLabels.delete( data.attributeOldValue );
					this._estruturaLabels.set( est, labels );
					for ( let idx = 0; idx < labels.length; idx++ ) {
						if ( labels[ idx ] ) {
							const disp = this._dispositivos.get( est.dispositivo );
							labels[ idx ].innerHTML = getEstruturaLabelStr( est, disp );
						} else {
							labels.splice( idx, 1 );
						}
					}
				} );
			} );
	}

	/** Creates a view element representing the estruturaLabel.
	 *
	 *	<span class="estruturaLabel"></span>
	 *
	 * @private
	 * @param {Element} modelItem
	 * @param {DowncastWriter} viewWriter
	 * @param {Boolean} [withLabel] Default false
	 * @returns
	 */
	_createEstruturaLabelViewElement( modelItem, viewWriter, withLabel = false ) {
		const dispositivos = this._dispositivos;
		const estruturaLabels = this._estruturaLabels;
		const cointainerView = viewWriter.createContainerElement( 'span', {
			class: ESTRUTURA_LABEL
		}, {
			isAllowedInsideAttributeElement: true
		} );

		if ( withLabel ) {
			const estruturaLabelView = viewWriter.createRawElement( 'span', {
				class: ESTRUTURA_LABEL
			}, function ( domElement ) {

				const parent = modelItem.findAncestor( ESTRUTURA );
				if ( parent ) {
					const est = parent.getAttribute( ESTRUTURA );
					const disp = dispositivos.get( est.dispositivo );

					if ( !estruturaLabels.has( est ) ) {
						estruturaLabels.set( est, [] );
					}
					estruturaLabels.get( est ).push( domElement );

					domElement.innerHTML = getEstruturaLabelStr( est, disp );
				} else {
					domElement.innerHTML = '<b>INVÁLIDO</b>';
				}
			} );

			viewWriter.insert( viewWriter.createPositionAt( cointainerView, 0 ), estruturaLabelView );
		}

		return cointainerView;
	}
}
